import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Truck, AlertTriangle, Clock, Package, DollarSign, TrendingUp, RefreshCw } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';
import { toast } from 'sonner';
import { AlertCircle } from 'lucide-react';

const Kpi = ({ icon: Icon, label, value, sub, tone }) => (
  <Card className="industrial-card">
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
        <Icon className={`w-4 h-4 ${tone || 'text-slate-400'}`} />
      </div>
      <div className="text-3xl font-bold mt-1" data-testid={`shipping-kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </CardContent>
  </Card>
);

const ShippingAnalyticsPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [byCarrier, setByCarrier] = useState(null);
  const [late, setLate] = useState(null);
  const [variance, setVariance] = useState(null);
  const [weekly, setWeekly] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, c, l, v, w, r] = await Promise.all([
        api().get('/shipping-analytics/overview'),
        api().get('/shipping-analytics/by-carrier?window_days=90'),
        api().get('/shipping-analytics/late?limit=25'),
        api().get('/shipping-analytics/rate-variance?threshold_pct=20'),
        api().get('/shipping-analytics/weekly-trend?weeks=12'),
        api().get('/shipping-analytics/repair-revenue?months=6'),
      ]);
      setOverview(o.data); setByCarrier(c.data); setLate(l.data);
      setVariance(v.data); setWeekly(w.data); setRevenue(r.data);
    } catch { toast.error('Failed to load shipping analytics'); }
    finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="p-6 text-slate-500" data-testid="shipping-analytics-loading">Loading shipping analytics…</div>;
  }

  const o = overview || {};
  const otr = o.on_time_rate_pct;
  const otrTone = otr === null || otr === undefined ? 'text-slate-400'
    : otr >= 95 ? 'text-emerald-600' : otr >= 80 ? 'text-amber-600' : 'text-red-600';

  const weeklyRows = (weekly?.rows || []).map(r => ({ ...r, label: r.week_of.slice(5) }));
  const revenueRows = (revenue?.rows || []).map(r => ({ ...r, label: r.month }));

  return (
    <div className="space-y-5" data-testid="shipping-analytics-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2"><TrendingUp className="w-7 h-7 text-slate-700" /> Shipping Analytics</h1>
          <p className="text-sm text-slate-500 mt-1">On-time performance, carrier scorecards, and repair revenue — 30 to 90-day rolling windows.</p>
        </div>
        <Button variant="outline" onClick={load} className="rounded-sm"><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi icon={Truck} label="Shipments (30d)" value={o.bols_in_window || 0} sub={`${o.bols_delivered || 0} delivered · ${o.packing_slips_shipped_in_window || 0} PS shipped`} />
        <Kpi icon={Clock} label="On-Time Rate" value={otr === null || otr === undefined ? '—' : `${otr}%`} sub={o.on_time_sample ? `${o.on_time_sample} delivered sample` : 'No deliveries yet in window'} tone={otrTone} />
        <Kpi icon={AlertTriangle} label="Late Shipments" value={o.late_count || 0} sub={`BoLs past ${o.late_threshold_days || 7} days from ship date`} tone={(o.late_count || 0) > 0 ? 'text-red-500' : 'text-emerald-500'} />
        <Kpi icon={DollarSign} label="Shipping Spend (30d)" value={`$${Number(o.shipping_spend_in_window || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} sub={`${o.shipping_spend_sample || 0} BoL${(o.shipping_spend_sample || 0) === 1 ? '' : 's'} w/ costs logged`} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi icon={Package} label="Pieces Shipped (30d)" value={Number(o.pieces_in_window || 0).toLocaleString()} />
        <Kpi icon={Package} label="Weight Shipped (30d)" value={`${Number(o.weight_lbs_in_window || 0).toLocaleString()} lbs`} />
        <Kpi icon={Clock} label="Avg Transit Days" value={o.avg_transit_days !== null && o.avg_transit_days !== undefined ? o.avg_transit_days : '—'} sub="pick-up → delivery" />
        <Kpi icon={DollarSign} label="Repair Revenue (30d)" value={`$${Number(o.repair_revenue_in_window || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} sub={`${o.repair_count_in_window || 0} completed repairs (non-warranty)`} />
      </div>

      <Card className="industrial-card">
        <CardHeader className="border-b border-slate-100 pb-3"><CardTitle className="text-lg">Weekly Shipment Trend — last 12 weeks</CardTitle></CardHeader>
        <CardContent className="p-4">
          {weeklyRows.length ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={weeklyRows}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="shipments" fill="#0f172a" name="Shipments" />
                <Bar dataKey="pieces" fill="#f59e0b" name="Pieces" />
              </BarChart>
            </ResponsiveContainer>
          ) : <div className="text-center text-sm text-slate-500 py-8">No shipping data in the last 12 weeks</div>}
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-4">
        <Card className="industrial-card">
          <CardHeader className="border-b border-slate-100 pb-3"><CardTitle className="text-lg">Carrier Scorecard — last 90 days</CardTitle></CardHeader>
          <CardContent className="p-0">
            {(byCarrier?.rows || []).length ? (
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Carrier</TableHead>
                  <TableHead className="text-right">Shipments</TableHead>
                  <TableHead className="text-right">On-Time</TableHead>
                  <TableHead className="text-right">Avg Transit</TableHead>
                  <TableHead className="text-right">Total Spend</TableHead>
                  <TableHead className="text-right">$/pc</TableHead>
                  <TableHead className="text-right">$/lb</TableHead>
                  <TableHead className="text-right">Late</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {byCarrier.rows.map(r => {
                    const ot = r.on_time_rate_pct;
                    const tone = ot === null || ot === undefined ? 'text-slate-400'
                      : ot >= 95 ? 'text-emerald-600 font-semibold'
                      : ot >= 80 ? 'text-amber-600 font-semibold'
                      : 'text-red-600 font-semibold';
                    const spend = Number(r.total_cost || 0);
                    return (
                      <TableRow key={r.carrier} data-testid={`shipping-carrier-row-${r.carrier}`}>
                        <TableCell className="font-semibold">{r.carrier}</TableCell>
                        <TableCell className="text-right mono">{r.shipments}</TableCell>
                        <TableCell className={`text-right mono ${tone}`}>{ot === null || ot === undefined ? '—' : `${ot}%`}</TableCell>
                        <TableCell className="text-right mono">{r.avg_transit_days ?? '—'}d</TableCell>
                        <TableCell className="text-right mono">{spend > 0 ? `$${spend.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : <span className="text-slate-300">—</span>}</TableCell>
                        <TableCell className="text-right mono">{r.cost_per_piece ? `$${r.cost_per_piece.toFixed(2)}` : <span className="text-slate-300">—</span>}</TableCell>
                        <TableCell className="text-right mono">{r.cost_per_lb ? `$${r.cost_per_lb.toFixed(2)}` : <span className="text-slate-300">—</span>}</TableCell>
                        <TableCell className="text-right">{r.late ? <Badge className="bg-red-100 text-red-700 border-red-200 border rounded-sm text-xs">{r.late}</Badge> : <span className="text-slate-300">—</span>}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : <div className="p-8 text-center text-sm text-slate-500">No shipments to score yet</div>}
          </CardContent>
        </Card>

        <Card className="industrial-card">
          <CardHeader className="border-b border-slate-100 pb-3"><CardTitle className="text-lg flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-red-500" /> Late Shipments to Action</CardTitle></CardHeader>
          <CardContent className="p-0">
            {(late?.rows || []).length ? (
              <Table>
                <TableHeader><TableRow>
                  <TableHead>BOL</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Carrier</TableHead>
                  <TableHead>Ship Date</TableHead>
                  <TableHead className="text-right">Overdue</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {late.rows.map(b => (
                    <TableRow key={b.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/orders/bol/${b.id}`)} data-testid={`shipping-late-row-${b.bol_number}`}>
                      <TableCell className="font-semibold mono">{b.bol_number}</TableCell>
                      <TableCell className="text-xs">{b.customer_name_snapshot}</TableCell>
                      <TableCell className="text-xs">{b.carrier || <span className="text-slate-400">—</span>}</TableCell>
                      <TableCell className="text-xs">{b.ship_date}</TableCell>
                      <TableCell className="text-right"><Badge className="bg-red-100 text-red-700 border-red-200 border rounded-sm text-xs">+{b.days_overdue}d</Badge></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : <div className="p-8 text-center text-sm text-emerald-600">All current shipments are on schedule.</div>}
          </CardContent>
        </Card>
      </div>

      <Card className="industrial-card border-amber-200 border">
        <CardHeader className="border-b border-amber-100 pb-3 bg-amber-50/60">
          <CardTitle className="text-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-amber-600" /> Rate Variance Alerts
            <span className="text-xs font-normal text-slate-600 ml-1">— BoLs priced ≥20% above the carrier's 90-day average $/piece</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {(variance?.rows || []).length ? (
            <Table>
              <TableHeader><TableRow>
                <TableHead>BOL</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Carrier</TableHead>
                <TableHead className="text-right">This BoL $/pc</TableHead>
                <TableHead className="text-right">Carrier Avg $/pc</TableHead>
                <TableHead className="text-right">Variance</TableHead>
                <TableHead className="text-right">Est. Overcharge</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {variance.rows.map(r => (
                  <TableRow key={r.id} className="cursor-pointer hover:bg-amber-50/40" onClick={() => navigate(`/orders/bol/${r.id}`)} data-testid={`shipping-variance-row-${r.bol_number}`}>
                    <TableCell className="font-semibold mono">{r.bol_number}</TableCell>
                    <TableCell className="text-xs">{r.customer_name_snapshot}</TableCell>
                    <TableCell className="text-xs">{r.carrier}</TableCell>
                    <TableCell className="text-right mono">${r.bol_cost_per_piece.toFixed(2)}</TableCell>
                    <TableCell className="text-right mono text-slate-500">${r.carrier_avg_cost_per_piece.toFixed(2)} <span className="text-[10px]">(n={r.carrier_sample_size})</span></TableCell>
                    <TableCell className="text-right"><Badge className="bg-red-100 text-red-700 border-red-200 border rounded-sm text-xs">+{r.variance_pct}%</Badge></TableCell>
                    <TableCell className="text-right mono font-semibold text-red-700">${r.overcharge_estimate.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : <div className="p-8 text-center text-sm text-emerald-600">No pricing anomalies detected — carrier rates look consistent.</div>}
        </CardContent>
      </Card>

      <Card className="industrial-card">
        <CardHeader className="border-b border-slate-100 pb-3"><CardTitle className="text-lg">Repair Revenue — last 6 months (non-warranty)</CardTitle></CardHeader>
        <CardContent className="p-4">
          {revenueRows.length ? (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={revenueRows}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 })}`} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="revenue" stroke="#0f172a" strokeWidth={2} name="Revenue" />
                <Line type="monotone" dataKey="warranty_waived" stroke="#f59e0b" strokeWidth={2} strokeDasharray="4 4" name="Warranty waived" />
              </LineChart>
            </ResponsiveContainer>
          ) : <div className="text-center text-sm text-slate-500 py-8">No completed repairs in the last 6 months</div>}
        </CardContent>
      </Card>
    </div>
  );
};

export default ShippingAnalyticsPage;
